import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dumbbell, ExternalLink, RotateCcw, MessageSquareText, AlertTriangle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TerugNaarVraag } from "@/components/TerugNaarVraag";
import api from "@/lib/api";

const GEWICHTEN = [
  { id: "lt10", label: "Minder dan 10 kg", score: 0 },
  { id: "10-15", label: "10–15 kg", score: 1 },
  { id: "15-23", label: "15–23 kg", score: 2 },
  { id: "gt23", label: "Meer dan 23 kg", score: 3 },
];
const FREQUENTIES = [
  { id: "af_en_toe", label: "Af en toe tillen", score: 0 },
  { id: "regelmatig", label: "Regelmatig tillen", score: 1 },
  { id: "vaak", label: "Vaak tillen", score: 2 },
  { id: "continu", label: "Vrijwel continu tillen", score: 3 },
];
const HOOGTES = [
  { id: "vloer", label: "Vanaf de vloer", score: 1 },
  { id: "knie_schouder", label: "Tussen knie en schouder", score: 0 },
  { id: "boven_schouder", label: "Boven schouderhoogte", score: 1 },
];
const FACTOREN = [
  { id: "draaien", label: "Draaien tijdens het tillen", score: 1 },
  { id: "reiken", label: "Ver reiken tijdens het tillen", score: 1 },
  { id: "ruimte", label: "Weinig werkruimte", score: 1 },
  { id: "hulpmiddelen", label: "Geen hulpmiddelen beschikbaar", score: 1 },
];
const LINKS = [
  { naam: "Arboportaal – NIOSH-methode", url: "https://www.arboportaal.nl/externe-bronnen/instrumenten/niosh-methode" },
  { naam: "FNV Tiltest", url: "https://www.fnv.nl/werk-inkomen/veilig-gezond-werken/lichamelijke-belasting/tillen-en-dragen/tiltest" },
  { naam: "TNO – Fysieke belasting (MMH-calculator)", url: "https://www.fysiekebelasting.tno.nl" },
];
const UITKOMSTEN = {
  groen: { titel: "Lage fysieke belasting", kleur: "border-emerald-300 bg-emerald-50 text-emerald-900",
    tekst: "Op basis van jouw antwoorden lijkt de fysieke belasting beperkt." },
  geel: { titel: "Verhoogde fysieke belasting", kleur: "border-amber-300 bg-amber-50 text-amber-900",
    tekst: "Op basis van jouw antwoorden kan sprake zijn van verhoogde fysieke belasting. Het kan aanleiding zijn om de situatie te bespreken met je leidinggevende of preventiemedewerker." },
  rood: { titel: "Mogelijk hoge fysieke belasting", kleur: "border-red-300 bg-red-50 text-red-900",
    tekst: "Op basis van jouw antwoorden kan sprake zijn van een hoge fysieke belasting. Het kan verstandig zijn dit te bespreken met je leidinggevende, preventiemedewerker, OR/VGWM, bedrijfsarts of vakbond." },
};

const Keuze = ({ actief, onClick, label, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`w-full text-left text-sm px-3.5 py-2.5 rounded-lg border transition-colors ${actief ? "border-[#B9772B] bg-[#FBF3E3] text-[#5B3A22] font-medium" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
    {label}
  </button>
);

const Vraag = ({ nr, titel, sub, children }) => (
  <div className="zk-card p-5">
    <p className="text-sm font-semibold text-slate-800 mb-0.5">Vraag {nr}. {titel}</p>
    {sub && <p className="text-xs text-slate-400 mb-2.5">{sub}</p>}
    <div className="space-y-2 mt-2.5">{children}</div>
  </div>
);

export default function Tilcheck() {
  const navigate = useNavigate();
  const [gewicht, setGewicht] = useState(null);
  const [freq, setFreq] = useState([]);
  const [hoogte, setHoogte] = useState([]);
  const [factoren, setFactoren] = useState([]);
  const [uitkomst, setUitkomst] = useState(null);
  const [detail, setDetail] = useState(null);
  const [toonUitleg, setToonUitleg] = useState(false);
  const gestartGemeld = useRef(false);

  const meldGestart = () => {
    if (!gestartGemeld.current) {
      gestartGemeld.current = true;
      api.post("/tilcheck/log", { uitkomst: "gestart" }).catch(() => {});
    }
  };

  const toggle = (lijst, setter, id) => { meldGestart(); setter(lijst.includes(id) ? lijst.filter((x) => x !== id) : [...lijst, id]); };
  const compleet = gewicht && freq.length > 0 && hoogte.length > 0;

  const bereken = () => {
    // Scorelogica ongewijzigd; alleen de onderdelen worden bewaard voor de transparantie-uitleg.
    const gewichtItem = GEWICHTEN.find((g) => g.id === gewicht);
    const freqGekozen = FREQUENTIES.filter((f) => freq.includes(f.id));
    const freqZwaarste = freqGekozen.reduce((a, b) => (b.score > a.score ? b : a));
    const hoogteGekozen = HOOGTES.filter((h) => hoogte.includes(h.id));
    const factorGekozen = FACTOREN.filter((f) => factoren.includes(f.id));
    let score = gewichtItem.score;
    score += freqZwaarste.score;
    score += hoogteGekozen.reduce((a, h) => a + h.score, 0);
    score += factoren.length;
    const kleur = score <= 2 ? "groen" : score <= 5 ? "geel" : "rood";
    setUitkomst(kleur);
    setDetail({ gewichtItem, freqGekozen, freqZwaarste, hoogteGekozen, factorGekozen, totaal: score });
    setToonUitleg(false);
    // Anonieme pilotmeting: alleen afgerond + uitkomstkleur, geen antwoorden of persoonsgegevens.
    api.post("/tilcheck/log", { uitkomst: kleur }).catch(() => {});
    setTimeout(() => document.querySelector('[data-testid="tilcheck-uitkomst"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const reset = () => { setGewicht(null); setFreq([]); setHoogte([]); setFactoren([]); setUitkomst(null); setDetail(null); setToonUitleg(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const u = uitkomst && UITKOMSTEN[uitkomst];

  return (
    <div data-testid="tilcheck-pagina" className="space-y-4 animate-fade-up">
      <TerugNaarVraag />
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-600 flex items-center justify-center shadow-sm shrink-0">
          <Dumbbell className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-xl text-[#3A281B]">Tilcheck</h1>
          <p className="text-sm text-slate-500">Snelle indicatie van de fysieke belasting bij tilwerk. Signaleren en informeren — geen officiële ergonomische beoordeling.</p>
        </div>
      </div>

      <Vraag nr={1} titel="Hoe zwaar zijn de lasten die je meestal tilt?">
        {GEWICHTEN.map((g) => <Keuze key={g.id} testId={`til-gewicht-${g.id}`} actief={gewicht === g.id} onClick={() => { meldGestart(); setGewicht(g.id); }} label={g.label} />)}
      </Vraag>
      <Vraag nr={2} titel="Welke situaties komen tijdens jouw werk voor?" sub="Meerdere antwoorden mogelijk">
        {FREQUENTIES.map((f) => <Keuze key={f.id} testId={`til-freq-${f.id}`} actief={freq.includes(f.id)} onClick={() => toggle(freq, setFreq, f.id)} label={f.label} />)}
      </Vraag>
      <Vraag nr={3} titel="Waar til je regelmatig?" sub="Meerdere antwoorden mogelijk">
        {HOOGTES.map((h) => <Keuze key={h.id} testId={`til-hoogte-${h.id}`} actief={hoogte.includes(h.id)} onClick={() => toggle(hoogte, setHoogte, h.id)} label={h.label} />)}
      </Vraag>
      <Vraag nr={4} titel="Welke situaties zijn van toepassing?" sub="Meerdere antwoorden mogelijk">
        {FACTOREN.map((f) => <Keuze key={f.id} testId={`til-factor-${f.id}`} actief={factoren.includes(f.id)} onClick={() => toggle(factoren, setFactoren, f.id)} label={f.label} />)}
      </Vraag>

      <Button data-testid="tilcheck-bereken-btn" onClick={bereken} disabled={!compleet} className="zk-primary w-full sm:w-auto">
        Bekijk resultaat
      </Button>

      {u && (
        <div data-testid="tilcheck-uitkomst" className={`border rounded-md p-5 space-y-3 ${u.kleur}`}>
          <p className="font-semibold flex items-center gap-2">
            {uitkomst === "rood" && <AlertTriangle className="w-4 h-4" />}
            {uitkomst === "groen" ? "🟢" : uitkomst === "geel" ? "🟡" : "🔴"} {u.titel}
          </p>
          <p className="text-sm">{u.tekst}</p>
          <p className="text-xs opacity-80 border-t border-current/20 pt-2">
            Deze Tilcheck geeft alleen een indicatie en is geen officiële ergonomische beoordeling.
          </p>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1.5">Verder beoordelen (officiële instrumenten)</p>
            <ul className="space-y-1">
              {LINKS.map((l, i) => (
                <li key={i}>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" title="Opent in een nieuw tabblad" data-testid={`tilcheck-link-${i}`}
                     className="text-sm underline inline-flex items-center gap-1.5 hover:opacity-70">
                    <ExternalLink className="w-3.5 h-3.5" /> {l.naam}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          {detail && (
            <div className="border border-current/20 rounded-md bg-white/60">
              <button data-testid="tilcheck-uitleg-toggle" onClick={() => setToonUitleg(!toonUitleg)}
                className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-left">
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${toonUitleg ? "rotate-180" : ""}`} />
                Hoe komt deze uitkomst tot stand?
              </button>
              {toonUitleg && (
                <div data-testid="tilcheck-uitleg" className="px-3.5 pb-3.5 space-y-3 text-sm">
                  <ul className="space-y-1.5">
                    <li data-testid="tilcheck-uitleg-gewicht" className="flex justify-between gap-3">
                      <span className="min-w-0">Gewicht: {detail.gewichtItem.label}</span>
                      <span className="font-semibold whitespace-nowrap">{detail.gewichtItem.score} {detail.gewichtItem.score === 1 ? "punt" : "punten"}</span>
                    </li>
                    <li data-testid="tilcheck-uitleg-frequentie" className="flex justify-between gap-3">
                      <span className="min-w-0">Tilfrequentie: {detail.freqZwaarste.label.toLowerCase()}
                        {detail.freqGekozen.length > 1 && " (zwaarste keuze telt)"}</span>
                      <span className="font-semibold whitespace-nowrap">{detail.freqZwaarste.score} {detail.freqZwaarste.score === 1 ? "punt" : "punten"}</span>
                    </li>
                    {detail.hoogteGekozen.map((h) => (
                      <li key={h.id} data-testid={`tilcheck-uitleg-hoogte-${h.id}`} className="flex justify-between gap-3">
                        <span className="min-w-0">Tilhoogte: {h.label.toLowerCase()}</span>
                        <span className="font-semibold whitespace-nowrap">{h.score} {h.score === 1 ? "punt" : "punten"}</span>
                      </li>
                    ))}
                    {detail.factorGekozen.length > 0 ? detail.factorGekozen.map((f) => (
                      <li key={f.id} data-testid={`tilcheck-uitleg-factor-${f.id}`} className="flex justify-between gap-3">
                        <span className="min-w-0">Verzwarend: {f.label.toLowerCase()}</span>
                        <span className="font-semibold whitespace-nowrap">1 punt</span>
                      </li>
                    )) : (
                      <li data-testid="tilcheck-uitleg-factor-geen" className="flex justify-between gap-3">
                        <span className="min-w-0">Verzwarende omstandigheden: geen gekozen</span>
                        <span className="font-semibold whitespace-nowrap">0 punten</span>
                      </li>
                    )}
                  </ul>
                  <p data-testid="tilcheck-uitleg-totaal" className="flex justify-between gap-3 border-t border-current/20 pt-2 font-semibold">
                    <span>Totaalscore</span><span>{detail.totaal} punten</span>
                  </p>
                  <p data-testid="tilcheck-uitleg-grenzen" className="text-xs">
                    Grenswaarden: 0–2 punten = 🟢 lage belasting · 3–5 punten = 🟡 verhoogde belasting · 6 of meer punten = 🔴 mogelijk hoge belasting.
                  </p>
                  <p data-testid="tilcheck-uitleg-optelling" className="text-xs">
                    De punten worden opgeteld; combinaties tellen niet extra mee (geen vermenigvuldiging). Bij tilfrequentie telt alleen de zwaarste gekozen situatie.
                  </p>
                  <p data-testid="tilcheck-uitleg-verantwoording" className="text-xs border-t border-current/20 pt-2">
                    De Tilcheck is een praktische signaleringscheck. De onderwerpen, zoals gewicht, tilhoogte, frequentie en verzwarende omstandigheden, sluiten aan op ergonomische en NIOSH-uitgangspunten. De exacte puntverdeling en grenswaarden zijn door Zoetwaren Kompas opgesteld en vormen geen officiële wettelijke norm of volledige NIOSH-berekening. Twijfel je, of wil je zekerheid? Dan is een volledige beoordeling nodig, bijvoorbeeld via de officiële instrumenten hierboven of via je preventiemedewerker of bedrijfsarts.
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button data-testid="tilcheck-naar-vraag-btn" onClick={() => navigate("/")}
              className="bg-[#B9772B] hover:bg-[#8a5a20] text-white w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-[#B9772B] focus-visible:ring-offset-2">
              <MessageSquareText className="w-4 h-4 mr-2" /> Stel een vraag
            </Button>
            <Button data-testid="tilcheck-reset-btn" onClick={reset} variant="outline"
              className="bg-white border-2 border-[#B9772B] text-[#4A3122] hover:bg-[#FBF3E3] hover:text-[#4A3122] w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-[#B9772B] focus-visible:ring-offset-2">
              <RotateCcw className="w-4 h-4 mr-2" /> Opnieuw invullen
            </Button>
          </div>
        </div>
      )}
      <TerugNaarVraag onderaan />
    </div>
  );
}
